import { type EnterpriseThemeContract, FontLoader } from "@bestowinc/kindred-ds";
import { useMemo } from "@bestowinc/tinkertoy-shell-deps/react";
import type { ReactNode } from "@bestowinc/tinkertoy-shell-deps/react";
import { ThemeProvider } from "@bestowinc/tinkertoy-shell-deps/styled-components";

import { ShellGlobalStyle } from "./ShellGlobalStyle";
import type { ModuleLoader } from "./module-loader";
import { ModuleLoaderProvider } from "./module-loader/module-loader-context";
import { ShellRuntimeProvider } from "./shell-runtime";
import type { ShellRuntimeConfig } from "./shell-runtime";
import type { ViewLoader } from "./view-loader";
import { ViewLoaderProvider } from "./view-loader";

export interface InternalTinkertoyProviderProps {
  /** Styled-components theme for Kindred + view modules. */
  theme: EnterpriseThemeContract;
  /** CDN module loader consumed via {@link useModuleLoader}. */
  moduleLoader: ModuleLoader;
  /**
   * CDN view loader consumed via {@link useViewLoader}, used by
   * `TinkertoyShell` to import the matched view bundle. Optional: only the
   * catch-all view route needs it, so error-boundary-only test trees (which
   * never mount `TinkertoyShell`'s success path) may omit it.
   */
  viewLoader: ViewLoader;
  /**
   * Base URL for POSTs to the Tinkertoy server `/action` endpoint. Exposed
   * to descendants via {@link useShellRuntime}; constant for the lifetime
   * of the mounted shell.
   */
  apiBaseUrl: string;
  children: ReactNode;
}

/**
 * Single place that wires up every cross-cutting provider the Tinkertoy shell
 * owns — theme, module loader, shell runtime config, and (over time) anything
 * else every view needs in scope.
 *
 * Deliberately does **not** render the React Router `<RouterProvider>`. The
 * router is injected by whatever renders us — `ShellRoot` uses the real
 * browser router built by `createShellRouter`; tests (e.g.
 * `ShellErrorBoundary.test.tsx`) use `createMemoryRouter` so they can drive
 * loader failures and navigations directly. Keeping the router on the outside
 * lets both paths share the rest of the provider stack.
 *
 * Not part of the public API — do not export from the package root. Tests
 * import it directly from this module.
 */
export function InternalTinkertoyProvider({
  theme,
  moduleLoader,
  viewLoader,
  apiBaseUrl,
  children,
}: InternalTinkertoyProviderProps) {
  const runtime = useMemo<ShellRuntimeConfig>(
    () => ({ apiBaseUrl }),
    [apiBaseUrl],
  );

  // The shell mounts no `<CarrierProvider>`: carrier context is a per-module
  // concern now. Each module bundles its own provider+hook and self-wraps its
  // tree from server data, so the shell can't (and must not) own that context
  return (
    <ThemeProvider theme={theme}>
      {/*
       * Global reset + typography normalization and web-font loading, applied
       * once at the shell root. Without these, text not wrapped in a Kindred
       * typography primitive falls back to the browser default serif and the
       * theme's fonts (e.g. Rotunda) are never fetched. Mirrors the Remix apps'
       * app-level GlobalStyle + KindredHead. FontLoader reads `theme.fontsToLoad`
       * from this same ThemeProvider.
       */}
      <ShellGlobalStyle />
      <FontLoader />
      <ShellRuntimeProvider value={runtime}>
        <ModuleLoaderProvider loader={moduleLoader}>
          {viewLoader ? (
            <ViewLoaderProvider loader={viewLoader}>
              {children}
            </ViewLoaderProvider>
          ) : (
            children
          )}
        </ModuleLoaderProvider>
      </ShellRuntimeProvider>
    </ThemeProvider>
  );
}
